import * as Sentry from '@sentry/react';
import {
  db,
  SpraySignOff,
  SpraySignOffPayload,
  SpraySessionStart,
} from '../db';
import { sprayService } from './sprayService';
import { purgeCompletedSession } from './spraySessionCacheService';
import { operatorSpraySessionService } from './operatorSpraySessionService';
import { refreshMyOperatorCombos } from './operatorSpraySessionCacheService';

/**
 * Field Mode offline drainer for the Start + Complete queues.
 *
 * Two queues feed into one entry point (syncPendingSprayCompletions):
 *
 *   spraySessionStarts  — payloads queued by spraySessionStartService
 *                         when the operator taps Start while offline or
 *                         the online attempt failed with a network error.
 *   spraySignOffs       — completion payloads queued the same way for the
 *                         Complete form.
 *
 * The drainer groups pending entries by sessionId and applies these
 * rules:
 *
 *   - If both a Start AND a Complete are pending for the same session,
 *     **coalesce**: skip the /start call entirely and send a single
 *     /complete with both startTime and endTime. The Complete payload's
 *     own startTime overrides the Start's startedAt when both are set —
 *     this matches the operator's editable-Start-on-completion UX. On
 *     success, both queue entries are deleted.
 *
 *   - On any successful sync, queue entries are **deleted** (not marked
 *     'synced'). They are not interesting after success and would
 *     otherwise accumulate forever in IndexedDB.
 *
 *   - On a Complete success, the cached session is purged via
 *     spraySessionCacheService.purgeCompletedSession(). Completed
 *     sessions never reappear on /sessions/my.
 *
 *   - On a non-retryable backend rejection (4xx/5xx), a row is written
 *     to spraySyncErrors so the field UI can surface a dismissible
 *     banner ("Session was cancelled by an administrator while you were
 *     offline"), and the queue entry is deleted — retrying would not
 *     help.
 *
 *   - On a retryable network error, the queue entry is marked 'failed',
 *     retryCount is bumped, and after QUARANTINE_THRESHOLD attempts the
 *     entry is moved to 'quarantined' for offline diagnosis.
 *
 * Track: spray_session_workflow_20260427 / Phase 4, Task 4
 * (extends operator_completion_20260412).
 */

const QUARANTINE_THRESHOLD = 5;

let drainInFlight = false;

function isRetryableError(err: any): boolean {
  if (!err) return false;
  if (err.response) return false;
  if (err.cause && err.cause.response) return false;
  return true;
}

/**
 * True when the error carries an HTTP response with a 4xx status — the
 * server made a deliberate semantic rejection (PPE incomplete, session
 * cancelled, not assigned, etc.) and a retry will keep failing. Used by
 * the operator combo drainers to choose between `quarantined` (4xx) and
 * `failed` (5xx / network).
 *
 * 5xx and network errors are NOT fatal — they're transient and the next
 * drain tick should retry.
 */
function isFatalHttpError(err: any): boolean {
  const status: number | undefined =
    err?.response?.status ?? err?.cause?.response?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
}

function backendErrorMessage(err: any): string {
  return (
    err?.response?.data?.error ??
    err?.cause?.response?.data?.error ??
    err?.message ??
    'Unknown error'
  );
}

function backendErrorCode(err: any): string {
  const status = err?.response?.status ?? err?.cause?.response?.status;
  if (status === 403) return 'not_assigned';
  if (status === 409) return 'conflict';
  if (status === 400) return 'invalid_state';
  if (status >= 500) return 'server_error';
  return 'unknown_error';
}

async function recordSyncError(sessionId: string, err: any): Promise<void> {
  await db.spraySyncErrors.add({
    sessionId,
    errorCode: backendErrorCode(err),
    message: backendErrorMessage(err),
    createdAt: new Date().toISOString(),
  });
}

export async function queueSignOff(
  sessionId: string,
  payload: SpraySignOffPayload,
  lastError?: string
): Promise<number> {
  const id = await db.spraySignOffs.add({
    sessionId,
    payload,
    syncStatus: 'pending',
    retryCount: 0,
    createdAt: new Date().toISOString(),
    lastError,
  });
  return id as number;
}

async function loadPending(): Promise<{
  signOffs: SpraySignOff[];
  starts: SpraySessionStart[];
}> {
  const [signOffs, starts] = await Promise.all([
    db.spraySignOffs.where('syncStatus').anyOf(['pending', 'failed']).toArray(),
    db.spraySessionStarts.where('syncStatus').anyOf(['pending', 'failed']).toArray(),
  ]);
  return { signOffs, starts };
}

async function markFailedOrQuarantined(
  table: 'spraySignOffs' | 'spraySessionStarts',
  id: number,
  retryCount: number,
  errorMessage: string
): Promise<void> {
  const nextRetry = retryCount + 1;
  const nextStatus = nextRetry >= QUARANTINE_THRESHOLD ? 'quarantined' : 'failed';
  await (table === 'spraySignOffs' ? db.spraySignOffs : db.spraySessionStarts).update(id, {
    syncStatus: nextStatus,
    retryCount: nextRetry,
    lastAttemptAt: new Date().toISOString(),
    lastError: errorMessage,
  });
}

/**
 * Drain a coalesced Start+Complete pair: build the merged Complete
 * payload, send it, and on success delete BOTH queue entries.
 */
async function drainCoalesced(
  start: SpraySessionStart,
  signOff: SpraySignOff
): Promise<void> {
  // Backend's /complete promotes a `planned` session in one save when
  // BOTH startTime and a valid ppeChecklist are supplied — that's the
  // single-shot Start+Complete contract. PPE rides over from the queued
  // /start payload (it was checked client-side at Start time and is the
  // authoritative client-side gate). signOff.payload.ppeChecklist is
  // normally absent in the Phase-3 split flow, so the start payload is
  // the source of truth here.
  const merged: SpraySignOffPayload = {
    ...signOff.payload,
    startTime: signOff.payload.startTime ?? start.payload.startedAt,
    ppeChecklist: signOff.payload.ppeChecklist ?? start.payload.ppeChecklist,
  };
  await db.spraySessionStarts.update(start.id!, { syncStatus: 'syncing' });
  await db.spraySignOffs.update(signOff.id!, { syncStatus: 'syncing' });
  try {
    await sprayService.completeSession(signOff.sessionId, merged);
    await db.spraySessionStarts.delete(start.id!);
    await db.spraySignOffs.delete(signOff.id!);
    await purgeCompletedSession(signOff.sessionId);
  } catch (err: any) {
    if (isRetryableError(err)) {
      // Roll the Start back to pending so the next drain can retry the
      // coalesced pair without losing it. Mark only the Complete as
      // failed (so its retryCount is what we track).
      await db.spraySessionStarts.update(start.id!, { syncStatus: 'pending' });
      await markFailedOrQuarantined(
        'spraySignOffs',
        signOff.id!,
        signOff.retryCount ?? 0,
        err?.message || 'Unknown error'
      );
    } else {
      // Non-retryable: write a sync-error, drop both entries, purge the
      // cache (the Complete attempt is over either way).
      await recordSyncError(signOff.sessionId, err);
      await db.spraySessionStarts.delete(start.id!);
      await db.spraySignOffs.delete(signOff.id!);
      await purgeCompletedSession(signOff.sessionId);
      Sentry.captureException(err);
    }
  }
}

async function drainSignOff(entry: SpraySignOff): Promise<void> {
  if (entry.id == null) return;
  await db.spraySignOffs.update(entry.id, { syncStatus: 'syncing' });
  try {
    await sprayService.completeSession(entry.sessionId, entry.payload);
    await db.spraySignOffs.delete(entry.id);
    await purgeCompletedSession(entry.sessionId);
  } catch (err: any) {
    if (isRetryableError(err)) {
      await markFailedOrQuarantined(
        'spraySignOffs',
        entry.id,
        entry.retryCount ?? 0,
        err?.message || 'Unknown error'
      );
    } else {
      await recordSyncError(entry.sessionId, err);
      await db.spraySignOffs.delete(entry.id);
      await purgeCompletedSession(entry.sessionId);
      Sentry.captureException(err);
    }
  }
}

async function drainStart(entry: SpraySessionStart): Promise<void> {
  if (entry.id == null) return;
  await db.spraySessionStarts.update(entry.id, { syncStatus: 'syncing' });
  try {
    await sprayService.startSession(entry.sessionId, entry.payload);
    await db.spraySessionStarts.delete(entry.id);
  } catch (err: any) {
    if (isRetryableError(err)) {
      await markFailedOrQuarantined(
        'spraySessionStarts',
        entry.id,
        entry.retryCount ?? 0,
        err?.message || 'Unknown error'
      );
    } else {
      await recordSyncError(entry.sessionId, err);
      await db.spraySessionStarts.delete(entry.id);
      Sentry.captureException(err);
    }
  }
}

/**
 * Drain queued operator combo /start submissions, in createdAt order.
 *
 * On a successful drain we delete the queue entry; on a 4xx we move the
 * entry to `quarantined` (the server has made a deliberate decision —
 * retrying will not help) AND revert the optimistic in_progress flip
 * back to not_started so the cache reflects server truth. Without the
 * revert the operator's cache says in_progress while the server says
 * not_started — every subsequent /complete or /end-early then fails
 * with "Invalid transition" because the server's combo is still
 * not_started.
 *
 * On a 5xx or network error we leave the queue entry at `failed` so
 * the next tick retries it, and DO NOT revert the cache — the start
 * may yet land successfully.
 *
 * After the loop, IF at least one drain succeeded we call
 * refreshMyOperatorCombos() once to pull server-truth into the cache.
 * Refresh is best-effort: if the network drops between the successful
 * drain and the refresh attempt, the optimistic cached rows survive and
 * the next refresh tick reconciles them.
 *
 * Phase 6 / Task 6.10. Revert-on-fatal added during Phase 6 QA after
 * operators got stuck with a locally-in_progress combo that the server
 * had never accepted (parent session deleted before /start landed was
 * the canonical repro).
 */
async function drainOperatorComboStarts(): Promise<void> {
  const pending = await db.operatorComboStarts
    .where('syncStatus')
    .anyOf(['pending', 'failed'])
    .sortBy('createdAt');

  let successCount = 0;
  for (const entry of pending) {
    if (entry.localId == null) continue;
    try {
      await db.operatorComboStarts.update(entry.localId, { syncStatus: 'syncing' });
      // The queued payload uses the new categorical ComboWeatherSnapshot
      // (defined in db.ts); the operator service still types its
      // signatures against the numeric variant from sprayService. Both
      // shapes flow through axios untouched, so we cast at the drainer
      // boundary. The Phase 6 follow-up reconciles the two interfaces.
      await operatorSpraySessionService.startCombo(
        entry.comboId,
        entry.payload as unknown as Parameters<
          typeof operatorSpraySessionService.startCombo
        >[1],
      );
      await db.operatorComboStarts.delete(entry.localId);
      successCount++;
    } catch (err: any) {
      const fatal = isFatalHttpError(err);
      if (fatal) {
        const cached = await db.operatorCombos.get(entry.comboId);
        if (cached) {
          await db.operatorCombos.put({
            ...cached,
            status: 'not_started',
            startedAt: undefined,
            cachedAt: new Date().toISOString(),
          });
        }
      }
      await db.operatorComboStarts.update(entry.localId, {
        syncStatus: fatal ? 'quarantined' : 'failed',
        lastError: backendErrorMessage(err),
      });
      Sentry.captureException(err);
    }
  }

  if (successCount > 0) {
    try {
      await refreshMyOperatorCombos();
    } catch {
      // Refresh is best-effort. The optimistic cache stays accurate
      // until the next tick reconciles with the server.
    }
  }
}

/**
 * Drain queued operator combo terminal submissions (/complete |
 * /end-early | /abandon), in createdAt order. The `action`
 * discriminator on each entry picks which operator service method runs.
 *
 * On a successful terminal sync we leave the cached combo row in place
 * (status is already 'completed' / 'incomplete' / 'cancelled' from the
 * optimistic `applyLocalTerminalCombo` write). This lets the operator
 * keep seeing the terminal state on the workflow page after Submit —
 * without it, useLiveQuery returns undefined and the page falls back to
 * the 'loading' spinner until a manual reload.
 *
 * The row will be removed naturally on the next call to
 * `refreshMyOperatorCombos` (e.g. next nav to /my) because the server
 * stops returning terminal combos.
 *
 * 4xx → quarantined AND the optimistic terminal flip is rolled back to
 *       `in_progress`. Without the rollback the cached combo is stuck in
 *       a terminal state with a rejected submission attached — the
 *       operator sees the rejection banner but the form is locked.
 *       Reverting lets them edit and resubmit. Terminal actions only
 *       ever happen from `in_progress` upstream, so this is always the
 *       correct revert target.
 * 5xx / network → failed (transient; next tick retries).
 *
 * Phase 6 / Task 6.10. Purge-after-success behaviour removed during
 * Phase 6 QA — see bug #9 (spinner-after-submit). Revert-on-fatal added
 * during Phase 6 QA after operators got stuck in a "completed" state
 * with an unrecoverable rejection (tankCount < 0.1 was the canonical
 * repro).
 */
async function drainOperatorComboCompletes(): Promise<void> {
  const pending = await db.operatorComboCompletes
    .where('syncStatus')
    .anyOf(['pending', 'failed'])
    .sortBy('createdAt');

  for (const entry of pending) {
    if (entry.localId == null) continue;
    try {
      await db.operatorComboCompletes.update(entry.localId, { syncStatus: 'syncing' });
      // Same shape-reconciliation cast as drainOperatorComboStarts —
      // queued payloads use the new categorical weather snapshot from
      // db.ts, the service signatures reference the numeric variant
      // from sprayService.ts. The wire shape is forwarded unmodified.
      switch (entry.action) {
        case 'complete':
          await operatorSpraySessionService.completeCombo(
            entry.comboId,
            entry.payload as unknown as Parameters<
              typeof operatorSpraySessionService.completeCombo
            >[1],
          );
          break;
        case 'end-early':
          await operatorSpraySessionService.endEarlyCombo(
            entry.comboId,
            entry.payload as unknown as Parameters<
              typeof operatorSpraySessionService.endEarlyCombo
            >[1],
          );
          break;
        case 'abandon':
          await operatorSpraySessionService.abandonCombo(
            entry.comboId,
            entry.payload as unknown as Parameters<
              typeof operatorSpraySessionService.abandonCombo
            >[1],
          );
          break;
        default: {
          const _exhaustive: never = entry.action;
          throw new Error(`Unhandled combo action: ${_exhaustive}`);
        }
      }
      await db.operatorComboCompletes.delete(entry.localId);
    } catch (err: any) {
      const fatal = isFatalHttpError(err);
      if (fatal) {
        const cached = await db.operatorCombos.get(entry.comboId);
        if (cached) {
          await db.operatorCombos.put({
            ...cached,
            status: 'in_progress',
            endedAt: undefined,
            cachedAt: new Date().toISOString(),
          });
        }
      }
      await db.operatorComboCompletes.update(entry.localId, {
        syncStatus: fatal ? 'quarantined' : 'failed',
        lastError: backendErrorMessage(err),
      });
      Sentry.captureException(err);
    }
  }
}

export async function syncPendingSprayCompletions(): Promise<void> {
  if (drainInFlight) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  drainInFlight = true;

  try {
    const { signOffs, starts } = await loadPending();

    // Build a sessionId → Start lookup so coalescing is O(n) on the
    // signOffs walk. The signOffs array is iterated directly to avoid
    // Map iteration (TS downlevel-iteration constraint with the
    // current tsconfig target).
    const startsBySession = new Map<string, SpraySessionStart>();
    for (const s of starts) startsBySession.set(s.sessionId, s);

    const handledStartIds = new Set<number>();
    const handledSignOffIds = new Set<number>();

    // Coalesced pairs first.
    for (const signOff of signOffs) {
      const start = startsBySession.get(signOff.sessionId);
      if (!start) continue;
      await drainCoalesced(start, signOff);
      if (start.id != null) handledStartIds.add(start.id);
      if (signOff.id != null) handledSignOffIds.add(signOff.id);
    }

    // Solo Complete entries (no matching Start in flight).
    for (const c of signOffs) {
      if (c.id != null && handledSignOffIds.has(c.id)) continue;
      await drainSignOff(c);
    }

    // Solo Start entries (no matching Complete in flight).
    for (const s of starts) {
      if (s.id != null && handledStartIds.has(s.id)) continue;
      await drainStart(s);
    }

    // ---- Operator combo queues (Phase 6 / Task 6.10) ------------------
    // Order invariant: Starts FIRST, then Completes. A queued Complete
    // for combo X always implies the Start either already landed on the
    // server OR is also queued (and will be processed in this pass). We
    // run both legs sequentially inside the same drainInFlight window so
    // the 30s heartbeat / `online` listener never double-fires.
    await drainOperatorComboStarts();
    await drainOperatorComboCompletes();
  } finally {
    drainInFlight = false;
  }
}

export function __resetSpraySyncFlagsForTesting(): void {
  drainInFlight = false;
}
